// "Set up" for a paid hosting or email purchase the customer never finished —
// Loot -> Products, a row marked "Paid — setup not finished".
//
// The customer does this in the dashboard wizard after checkout. Staff could
// only do it by impersonating, and impersonation blocks both wizards. This
// collects the same fields, shows what will happen, and submits. The backend
// runs the customer's own submit as the setup's owner (same domain checks,
// payment check, provisioning) and logs it to Events against the staffer.

import { useState } from 'react';
import { submitPendingSetup } from '../api';
import { hostingNeedsDomain, validateHostingSetup, validateEmailSetup } from '../lib/setupForms';

const btn = { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const primary = { ...btn, background: 'var(--accent)', color: '#fff', border: 'none' };
const input = { padding: '5px 7px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)', outline: 'none', width: 220 };
const label = { fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', display: 'block', marginBottom: 2 };
const heading = { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 6 };

function Field({ name, text, value, onChange, placeholder, width }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={label}>{text}</span>
      <input aria-label={text} name={name} value={value} placeholder={placeholder} onChange={e => onChange(name, e.target.value)} style={{ ...input, ...(width ? { width } : {}) }} />
    </label>
  );
}

function summarise(kind, fields, product) {
  if (kind === 'hosting') {
    return fields.domain
      ? `Build ${product.label || 'the hosting plan'} on ${fields.domain}. If we hold the domain its DNS is pointed here; if it's registered elsewhere you'll get the records to give the customer.`
      : `Build ${product.label || 'the hosting plan'} on a temporary address. Use Go live on the new row once the customer has a domain.`;
  }
  if (fields.branch === 'new-tenant') {
    return `Create a new Microsoft 365 tenant ${fields.onmicrosoftPrefix}.onmicrosoft.com for ${fields.domain}, with admin user "${fields.adminUpn}" (${fields.adminFirstName} ${fields.adminLastName}), and order ${product.quantity || 1} seat(s).`;
  }
  return `Order ${product.quantity || 1} seat(s) on the customer's existing tenant ${fields.customerTenantId} for ${fields.domain} (tenant admin ${fields.customerAdminEmail}).`;
}

export default function PendingSetupForm({ accountId, product, onDone }) {
  const kind = product.productKind;
  const [form, setForm] = useState(kind === 'email' ? { branch: 'new-tenant', adminUpn: 'admin' } : {});
  const [confirming, setConfirming] = useState(null);   // validated fields
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function review() {
    const { fields, error: err } = kind === 'hosting'
      ? validateHostingSetup({ domain: form.domain, setupType: product.setupType })
      : validateEmailSetup(form);
    setError(err);
    if (!err) setConfirming(fields);
  }

  async function submit() {
    setBusy(true); setError('');
    try {
      setResult(await submitPendingSetup(accountId, kind, product.productId, confirming));
      setConfirming(null);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  if (result) {
    const records = Array.isArray(result.aRecords) ? result.aRecords : [];
    const done = kind === 'hosting'
      ? (result.alreadyProvisioned ? `Already set up${result.domain ? ` on ${result.domain}` : ''}.` : `Site built${result.domain ? ` on ${result.domain}` : ''}.`)
      : (result.alreadySubmitted ? 'Already submitted.' : `Email order placed${result.state ? ` (${result.state})` : ''}.`);
    return (
      <div style={{ padding: '8px 0 12px', fontSize: 12 }}>
        <div style={{ color: '#4ade80', fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 6 }}>✓ {done} Logged to this account's Events.</div>
        {result.externalRegistrar && records.length > 0 && (
          <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24', fontSize: 11 }}>
            {result.domain} is registered somewhere else, so it won't load until the customer points it here. At their registrar, set:
            <div style={{ fontFamily: 'var(--mono)', marginTop: 4, color: 'var(--text)' }}>
              {records.map(r => <div key={`${r.type}:${r.host}`}>{r.type} {r.host} → {r.value}</div>)}
            </div>
          </div>
        )}
        <button style={{ ...btn, marginTop: 8 }} onClick={() => onDone?.()}>Refresh products</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0 12px', fontSize: 12 }}>
      <div style={heading}>{kind === 'hosting' ? 'Set up hosting' : 'Set up Microsoft 365'}</div>
      {confirming ? (
        <div role="alertdialog" aria-label="Confirm setup" style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.3)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Finish this setup for the customer?</div>
          <div style={{ color: 'var(--text2)' }}>{summarise(kind, confirming, product)} This can take a minute or two.</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button data-write style={primary} disabled={busy} onClick={submit}>{busy ? 'Setting up…' : 'Set up'}</button>
            <button style={btn} disabled={busy} onClick={() => setConfirming(null)}>Back</button>
          </div>
        </div>
      ) : kind === 'hosting' ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field name="domain" text="Customer's domain" value={form.domain || ''} onChange={set} width={260}
            placeholder={hostingNeedsDomain(product.setupType) ? 'required, e.g. example.com' : 'blank = temporary address'} />
          <button data-write style={btn} onClick={review}>Review</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 12 }} role="radiogroup" aria-label="Tenant">
            {[['new-tenant', 'New Microsoft 365 tenant'], ['existing-tenant', 'Customer already has one']].map(([v, t]) => (
              <label key={v} style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="branch" checked={form.branch === v} onChange={() => set('branch', v)} /> {t}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Field name="domain" text="Mail domain" value={form.domain || ''} onChange={set} placeholder="example.com" />
            {form.branch === 'new-tenant' ? (
              <>
                <Field name="onmicrosoftPrefix" text="onmicrosoft name" value={form.onmicrosoftPrefix || ''} onChange={set} placeholder="acme" width={140} />
                <Field name="adminUpn" text="Admin username" value={form.adminUpn || ''} onChange={set} placeholder="admin" width={120} />
                <Field name="adminFirstName" text="Admin first name" value={form.adminFirstName || ''} onChange={set} width={140} />
                <Field name="adminLastName" text="Admin last name" value={form.adminLastName || ''} onChange={set} width={140} />
              </>
            ) : (
              <>
                <Field name="customerTenantId" text="Tenant id" value={form.customerTenantId || ''} onChange={set} placeholder="00000000-0000-0000-0000-000000000000" width={300} />
                <Field name="customerAdminEmail" text="Tenant admin email" value={form.customerAdminEmail || ''} onChange={set} placeholder="admin@example.com" />
              </>
            )}
          </div>
          <div><button data-write style={btn} onClick={review}>Review</button></div>
        </div>
      )}
      {error && <div style={{ color: '#f87171', fontSize: 11, marginTop: 6 }}>⚠ {error}</div>}
    </div>
  );
}
